import { useState, useEffect } from 'react';
import './CommentSection.css';

const CommentSection = ({ postId, postSlug }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState({
    author: { name: '', email: '', website: '' },
    content: ''
  });
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/comments/post/${postId}?status=approved`);
      const data = await response.json();
      
      if (data.success) {
        setComments(data.comments);
      } else {
        setError(data.error || 'Failed to load comments');
      }
    } catch (err) {
      setError('Failed to load comments. Please try again.');
      console.error('Comment fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setNewComment(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setNewComment(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newComment.author.name || !newComment.author.email || !newComment.content) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          postId,
          author: newComment.author,
          content: newComment.content,
          parentId: replyTo
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Comment submitted! It will appear after moderation.');
        setNewComment({
          author: { name: '', email: '', website: '' },
          content: ''
        });
        setReplyTo(null);
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        setError(data.error || 'Failed to submit comment');
      }
    } catch (err) {
      setError('Failed to submit comment. Please try again.');
      console.error('Comment submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderComment = (comment, depth = 0) => {
    const maxDepth = 3;
    const canReply = depth < maxDepth;

    return (
      <div key={comment._id} className={`comment depth-${depth}`}>
        <div className="comment-header">
          <div className="comment-avatar">
            {comment.author.avatar ? (
              <img src={comment.author.avatar} alt={comment.author.name} />
            ) : (
              <div className="avatar-placeholder">
                {comment.author.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="comment-meta">
            <strong className="comment-author">
              {comment.author.website ? (
                <a href={comment.author.website} target="_blank" rel="nofollow noopener noreferrer">
                  {comment.author.name}
                </a>
              ) : (
                comment.author.name
              )}
            </strong>
            <span className="comment-date">
              {new Date(comment.createdAt).toLocaleDateString('en-ZA', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
        </div>
        
        <div className="comment-content">
          {comment.content}
        </div>

        <div className="comment-actions">
          {canReply && (
            <button
              className="reply-btn"
              onClick={() => {
                setReplyTo(comment._id);
                window.scrollTo({
                  top: document.querySelector('.comment-form').offsetTop - 100,
                  behavior: 'smooth'
                });
              }}
            >
              Reply
            </button>
          )}
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="comments-section">
        <h2>Comments</h2>
        <div className="loading">Loading comments...</div>
      </div>
    );
  }

  return (
    <div className="comments-section">
      <h2>Comments ({comments.length})</h2>

      {error && !successMessage && (
        <div className="error-message">{error}</div>
      )}

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <form className="comment-form" onSubmit={handleSubmit}>
        <h3>{replyTo ? 'Reply to Comment' : 'Leave a Comment'}</h3>
        
        {replyTo && (
          <div className="reply-notice">
            Replying to comment
            <button
              type="button"
              className="cancel-reply"
              onClick={() => setReplyTo(null)}
            >
              Cancel
            </button>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={newComment.author.name}
              onChange={(e) => handleInputChange('author.name', e.target.value)}
              required
              placeholder="Your name"
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              value={newComment.author.email}
              onChange={(e) => handleInputChange('author.email', e.target.value)}
              required
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Website (optional)</label>
          <input
            type="url"
            value={newComment.author.website}
            onChange={(e) => handleInputChange('author.website', e.target.value)}
            placeholder="https://yourwebsite.com"
          />
        </div>

        <div className="form-group">
          <label>Comment *</label>
          <textarea
            value={newComment.content}
            onChange={(e) => handleInputChange('content', e.target.value)}
            required
            rows="5"
            maxLength="2000"
            placeholder="Share your thoughts..."
          />
          <div className="char-count">
            {newComment.content.length} / 2000
          </div>
        </div>

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit Comment'}
        </button>
      </form>

      <div className="comments-list">
        {comments.length === 0 ? (
          <p className="no-comments">No comments yet. Be the first to comment!</p>
        ) : (
          comments.map(comment => renderComment(comment))
        )}
      </div>
    </div>
  );
};

export default CommentSection;
